import { useMemo } from 'react';
import { isSome, Option } from 'fp-ts/lib/Option';
import i18n from 'asset/translation';
import gtm from 'adapter/in/reports/config/gtm';
import { BusinessInsight, Features } from 'domain/entity/feature';

export const ADD_WIDGET_MENU_KEY = {
    ANALYSIS: 'analysis',
    RICH_TEXT: 'rich-text',
} as const;

export const ALL_CATEGORY_KEY = 'all';

const BUSINESS_INSIGHT_MENU_KEY_PREFIX = 'business-insight';
const GROUP_MENU_KEY_PREFIX = 'group';

export type AddWidgetCatalogItem = {
    key: string;
    label: string;
    description?: string;
    dataId: string;
    categoryLabel: string;
    tagLabel: string;
    groupKey?: string;
};

export type AddWidgetGroup = {
    key: string;
    label: string;
    items: AddWidgetCatalogItem[];
};

export type AddWidgetCategory = {
    key: string;
    label: string;
    items: AddWidgetCatalogItem[];
    groups?: AddWidgetGroup[];
};

const getWidgetDescription = (descriptionKey: string): string | undefined => {
    const description = i18n.t(`widgets.addWidgetModal.descriptions.${descriptionKey}`, { defaultValue: '' });
    return description || undefined;
};

export const buildBusinessInsightMenuKey = (businessInsight: BusinessInsight) =>
    `${BUSINESS_INSIGHT_MENU_KEY_PREFIX}.${businessInsight.feature}.${businessInsight.type}`;

export const buildGroupMenuKey = (categoryKey: string, groupType: string) => `${GROUP_MENU_KEY_PREFIX}.${categoryKey}.${groupType}`;

export const getCatalogItemsForKey = (categories: AddWidgetCategory[], key: string): AddWidgetCatalogItem[] => {
    const matchingCategory = categories.find((category) => category.key === key);
    if (matchingCategory) return matchingCategory.items;

    const matchingGroup = categories.flatMap((category) => category.groups ?? []).find((group) => group.key === key);
    return matchingGroup?.items ?? [];
};

const buildStaticCategories = (): AddWidgetCategory[] => {
    const analyticsLabel = i18n.t('reports.reportTemplate.addWidgetModal.categories.analytics');
    const textLabel = i18n.t('reports.reportTemplate.addWidgetModal.categories.text');

    return [
        {
            key: 'analytics',
            label: analyticsLabel,
            items: [
                {
                    key: ADD_WIDGET_MENU_KEY.ANALYSIS,
                    label: i18n.t('advancedAnalytics.analysis.title'),
                    description: getWidgetDescription('analysis'),
                    dataId: gtm.addWidget,
                    categoryLabel: analyticsLabel,
                    tagLabel: analyticsLabel,
                },
            ],
        },
        {
            key: 'text',
            label: textLabel,
            items: [
                {
                    key: ADD_WIDGET_MENU_KEY.RICH_TEXT,
                    label: i18n.t('widgets.widgetMenu.text'),
                    description: getWidgetDescription('richText'),
                    dataId: gtm.addTextWidget,
                    categoryLabel: textLabel,
                    tagLabel: textLabel,
                },
            ],
        },
    ];
};

export const useAddWidgetCatalog = (features: Option<Features>) =>
    useMemo<{
        categories: AddWidgetCategory[];
        businessInsightsByMenuKey: Map<string, BusinessInsight>;
    }>(() => {
        const businessInsightsByMenuKey = new Map<string, BusinessInsight>();

        const featureCategories: AddWidgetCategory[] = isSome(features)
            ? features.value
                  .filter((feature) => feature.businessInsights.length > 0)
                  .map((feature) => {
                      const groupsByKey = new Map<string, AddWidgetGroup>();

                      const items = feature.businessInsights.map((businessInsight) => {
                          const key = buildBusinessInsightMenuKey(businessInsight);
                          businessInsightsByMenuKey.set(key, businessInsight);

                          const group = businessInsight.group;
                          const groupKey = group ? buildGroupMenuKey(feature.key, group.type) : undefined;

                          const item: AddWidgetCatalogItem = {
                              key,
                              label: businessInsight.label,
                              description: businessInsight.description || undefined,
                              dataId: `${gtm.addWidget}.${businessInsight.type}`,
                              categoryLabel: feature.name,
                              tagLabel: group ? `${feature.name} / ${group.label}` : feature.name,
                              groupKey,
                          };

                          if (group && groupKey) {
                              if (!groupsByKey.has(groupKey)) {
                                  groupsByKey.set(groupKey, { key: groupKey, label: group.label, items: [] });
                              }
                              groupsByKey.get(groupKey)?.items.push(item);
                          }

                          return item;
                      });

                      return {
                          key: feature.key,
                          label: feature.name,
                          items,
                          groups: groupsByKey.size > 0 ? Array.from(groupsByKey.values()) : undefined,
                      };
                  })
            : [];

        const categories = [...buildStaticCategories(), ...featureCategories];
        const allCategory: AddWidgetCategory = {
            key: ALL_CATEGORY_KEY,
            label: i18n.t('reports.reportTemplate.addWidgetModal.categories.all'),
            items: categories.flatMap((category) => category.items),
        };

        return {
            categories: [allCategory, ...categories],
            businessInsightsByMenuKey,
        };
    }, [features]);
